import { useEffect, useState, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { getBannerAd, bodyOrHtml } from '@Components/Utilities/SandhillsUtility';
import { getCookie, setCookie } from '@Components/Utilities/CookieHelper';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import Slide from '@mui/material/Slide';
import ConditionalWrapper from '@Components/Utilities/ConditionalWrapper';
import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import atoms from '@Components/Utilities/SandhillsAtoms';

const slotRenderEnded = {};
const topPopUpExpirationDays = 7;

function defineSlot(slotId, ad, adUnits, targeting, networkCode, currentUser) {
    if (ad && networkCode && adUnits?.length > 0) {
        window.googletag = window.googletag || { cmd: [] };
        if (!googletag.apiReady) {
            setTimeout(() => defineSlot(slotId, ad, adUnits, targeting, networkCode, currentUser), 100);
            return;
        }

        googletag.cmd.push(() => {
            googletag
                .defineSlot('/' + networkCode + '/' + ad.ParentPath[ad.ParentPath.length - 1].AdUnitCode + '/' + ad.AdUnitCode, [1, 1], slotId)
                .addService(googletag.pubads());
        });

        const slots = googletag.pubads().getSlots();
        if (slots.length > 0 && slots.length == adUnits.length) {
            googletag.cmd.push(() => {
                if (!googletag.pubadsReady) {
                    if (targeting) {
                        for (const target in targeting) {
                            googletag.pubads().setTargeting(target, targeting[target].join(','));
                        }
                    }

                    const location = currentUser?.CurrentLocation?.Coordinates;
                    if (location && (location.latitude || location.latitude == 0) && (location.longitude || location.longitude == 0)) {
                        googletag.pubads().setLocation(`${location.latitude}, ${location.longitude}`);
                    }

                    googletag.pubads().addEventListener('slotRenderEnded', (e) => {
                        const id = e.slot.getSlotElementId();
                        const banner = document.getElementById(id)?.parentElement;
                        if (e.isEmpty) {
                            const stopRefreshAttempts = slotRenderEnded[id];
                            if (stopRefreshAttempts) {
                                banner?.classList?.add('hidden');
                            } else {
                                googletag.pubads().refresh([e.slot]);
                            }

                            slotRenderEnded[id] = !stopRefreshAttempts;
                        } else {
                            slotRenderEnded[id] = false;
                            banner?.classList?.remove('hidden');
                        }
                    });

                    googletag.pubads().enableSingleRequest();
                    googletag.enableServices();
                }

                googletag.pubads().getSlots().forEach(slot => {
                    googletag.display(slot.getSlotElementId());
                });
            });
        }
    }
}

function refreshSlot(slotId, targeting, currentUser) {
    googletag.cmd.push(() => {
        googletag.pubads().clearTargeting();
        if (targeting) {
            for (const target in targeting) {
                googletag.pubads().setTargeting(target, targeting[target].join(','));
            }
        }

        const location = currentUser?.CurrentLocation?.Coordinates;
        if (location && (location.latitude || location.latitude == 0) && (location.longitude || location.longitude == 0)) {
            googletag.pubads().setLocation(`${location.latitude}, ${location.longitude}`);
        }

        const slot = googletag.pubads().getSlots().find(slot => slot.getSlotElementId() == slotId);
        if (slot) {
            googletag.pubads().refresh([slot]);
        }
    });
}

function destroySlot(slotId) {
    const slot = googletag?.pubads?.()?.getSlots?.()?.find(x => x.getSlotElementId() == slotId);
    if (slot) {
        googletag.cmd.push(() => {
            googletag?.destroySlots?.([slot]);
        });
    }
}

function getDoubleClickAd(ad, adUnits, position) {
    return ad || getBannerAd(adUnits, position);
}

function isTopPopUp(doubleClickAd) {
    switch (doubleClickAd?.AdGroup) {
        case 'Top_PopUp':
        case 'Top_PopUp_AuctionResults':
            return true;
        default:
            return false;
    }
}

export default function DoubleClickAd(props) {
    const {
        ad,
        style,
        position,
        contained,
        AdUnits,
        NetworkCode,
        Targeting
    } = props;

    const defined = useRef(false);
    const windowSize = useWindowSize();
    const currentUser = useRecoilValue(atoms.currentUser);
    const doubleClickAd = getDoubleClickAd(ad, AdUnits, position);
    const slotId = `div-gpt-ad-${doubleClickAd?.AdUnitCode}-${position}`;
    const topPopUp = isTopPopUp(doubleClickAd);
    const [topPopUpVisible, setTopPopUpVisible] = useState(false);

    useEffect(function refresh() {
        if (defined.current) {
            try {
                refreshSlot(slotId, Targeting, currentUser);
            } catch (error) {
                console.error(`Failed to refresh double click ads: ${error}`);
            }
        }
    }, [AdUnits, Targeting, windowSize.isMobile]);

    useEffect(function define() {
        try {
            defineSlot(slotId, doubleClickAd, AdUnits, Targeting, NetworkCode, currentUser);
            if (topPopUp) {
                window.addEventListener('scroll', topPopUpScroll);
            }
        } catch (error) {
            console.log(`Failed to load double click ads: ${error}`);
        } finally {
            defined.current = true;
        }

        return () => {
            destroySlot(slotId);
            if (topPopUp) {
                window.removeEventListener('scroll', topPopUpScroll);
            }
        };
    }, []);

    const topPopUpScroll = () => {
        if (bodyOrHtml().scrollTop > 100) {
            const topPopCookie = getCookie(doubleClickAd?.AdGroup);
            if (topPopCookie != 'true') {
                setTopPopUpVisible(true);
            }
        }
    };

    const closeTopPopUp = () => {
        const expiresOn = new Date(Date.now());
        expiresOn.setDate(expiresOn.getDate() + topPopUpExpirationDays);
        setCookie(doubleClickAd?.AdGroup, 'true', expiresOn.toUTCString());
        setTopPopUpVisible(false);
    };

    if (doubleClickAd) {
        return (
            <ConditionalWrapper
                condition={topPopUp}
                wrapper={children => (
                    <Slide
                        direction='down'
                        in={topPopUpVisible}
                        timeout={{
                            enter: 1000,
                            exit: 1000
                        }}
                    >
                        {children}
                    </Slide>
                )}
            >
                {contained ?
                    <div className='banner-ad-container' style={style}>
                        <div className={`DoubleClick_${doubleClickAd.AdGroup}`}>
                            {topPopUp &&
                                <button className='close-ad ts-button' type='button' aria-label={translator.translate('sandhills.tradepubwebsite.mvc.close')} onClick={closeTopPopUp}>
                                    <span className='visually-hidden'>
                                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.close' />
                                    </span>
                                </button>}
                            <div id={slotId} />
                        </div>
                    </div> :
                    <div className={`DoubleClick_${doubleClickAd.AdGroup}`}>
                        {topPopUp &&
                            <button className='close-ad ts-button' type='button' aria-label={translator.translate('sandhills.tradepubwebsite.mvc.close')} onClick={closeTopPopUp}>
                                <span className='visually-hidden'>
                                    <TranslationText tKey='sandhills.tradepubwebsite.mvc.close' />
                                </span>
                            </button>}
                        <div id={slotId} />
                    </div>}
            </ConditionalWrapper>
        );
    }

    return (
        <></>
    );
}